import Vue from 'vue';
import Decimal from 'decimal.js';
import store from '@/stores/store';
import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import { orgStorage } from '@/utils/org-storage';
import { resolvePermissionCheck } from '@/utils/groupPermissions';

const CART_PENDING_STORAGE_KEY = 'cart_pending_targets';

const toQtyDecimal = (value) => {
    if (value === null || value === undefined || value === '') {
        return new Decimal(0);
    }
    try {
        return new Decimal(String(value));
    } catch (e) {
        return new Decimal(0);
    }
};

const formatQty = (value) => toQtyDecimal(value).toDecimalPlaces(3, Decimal.ROUND_DOWN).toString();

const addQty = (left, right) => formatQty(toQtyDecimal(left).plus(toQtyDecimal(right)));

const cartGroupKey = (providerId, deliverysiteId) => {
    const ds = deliverysiteId == null || deliverysiteId === 'null'
        ? 'null'
        : String(Number(deliverysiteId));
    return `${Number(providerId)}:${ds}`;
};

const findCart = (cartsList, providerId, deliverysiteId) => {
    const key = cartGroupKey(providerId, deliverysiteId);
    return (cartsList || []).find(
        (cart) => cartGroupKey(cart.provider, cart.deliverysite) === key
    );
};

const resolveProviderId = (providerId, item) => {
    const candidates = [
        providerId,
        item?.provider,
        item?.catalogentry_data?.provider,
        item?.catalogentry_data?.provider_data?.id,
        item?.provider_data?.id,
    ];
    for (const candidate of candidates) {
        const parsed = Number(candidate);
        if (Number.isFinite(parsed) && parsed > 0) {
            return parsed;
        }
    }
    return null;
};

const pendingItemsExist = (pendingItems) => {
    for (const providerId in pendingItems || {}) {
        for (const deliverysiteKey in pendingItems[providerId] || {}) {
            if (Object.keys(pendingItems[providerId][deliverysiteKey] || {}).length > 0) {
                return true;
            }
        }
    }
    return false;
};

const pendingStorageUserId = () => store.state.session?.user?.id || null;

const persistPendingTargets = (state) => {
    try {
        const userId = pendingStorageUserId();
        if (!userId) {
            return;
        }
        const items = [];
        for (const providerId in state.pendingItems || {}) {
            for (const deliverysiteKey in state.pendingItems[providerId] || {}) {
                for (const catalogentryKey in state.pendingItems[providerId][deliverysiteKey] || {}) {
                    const pendingItem = state.pendingItems[providerId][deliverysiteKey][catalogentryKey];
                    const resolvedProviderId = resolveProviderId(providerId, pendingItem);
                    if (!resolvedProviderId) {
                        continue;
                    }
                    const catalogentry = parseInt(catalogentryKey, 10);
                    const deliverysiteId = deliverysiteKey === 'null'
                        ? null
                        : parseInt(deliverysiteKey, 10);
                    const cart = findCart(state.cartsList, providerId, deliverysiteId);
                    const existingItem = cart?.items?.find(
                        (item) => Number(item.catalogentry) === catalogentry && item.id
                    );
                    const targetQuantity = addQty(existingItem?.count || 0, pendingItem.quantity);
                    items.push({
                        providerId: resolvedProviderId,
                        deliverysiteId,
                        catalogentry,
                        targetQuantity,
                        catalogentry_data: pendingItem.catalogentry_data
                            || existingItem?.catalogentry_data
                            || null,
                    });
                }
            }
        }
        if (!items.length) {
            orgStorage.removeItem(CART_PENDING_STORAGE_KEY);
            return;
        }
        orgStorage.setItem(
            CART_PENDING_STORAGE_KEY,
            JSON.stringify({ userId, items })
        );
    } catch (e) {
        // Quota / private mode: keep in-memory pending only.
    }
};

const loadStoredPendingTargets = () => {
    try {
        const raw = orgStorage.getItem(CART_PENDING_STORAGE_KEY);
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw);
        const userId = pendingStorageUserId();
        if (!parsed || parsed.userId !== userId || !Array.isArray(parsed.items)) {
            return [];
        }
        return parsed.items;
    } catch (e) {
        return [];
    }
};

const pendingItemsFromTargets = (cartsList, targets) => {
    const pendingItems = {};
    for (const target of targets) {
        const providerId = resolveProviderId(
            target.providerId,
            { catalogentry_data: target.catalogentry_data }
        );
        if (!providerId) {
            continue;
        }
        const deliverysiteId = target.deliverysiteId == null
            ? null
            : Number(target.deliverysiteId);
        const catalogentry = Number(target.catalogentry);
        const cart = findCart(cartsList, providerId, deliverysiteId);
        const existingItem = cart?.items?.find(
            (item) => Number(item.catalogentry) === catalogentry && item.id
        );
        const delta = formatQty(
            toQtyDecimal(target.targetQuantity).minus(toQtyDecimal(existingItem?.count || 0))
        );
        if (toQtyDecimal(delta).eq(0)) {
            continue;
        }
        const deliverysiteKey = deliverysiteId ? String(deliverysiteId) : 'null';
        if (!pendingItems[providerId]) {
            pendingItems[providerId] = {};
        }
        if (!pendingItems[providerId][deliverysiteKey]) {
            pendingItems[providerId][deliverysiteKey] = {};
        }
        pendingItems[providerId][deliverysiteKey][String(catalogentry)] = {
            catalogentry,
            catalogentry_data: target.catalogentry_data || existingItem?.catalogentry_data || null,
            quantity: delta,
            deliverysite: deliverysiteId,
        };
    }
    return pendingItems;
};

// Timeout for creating pending items
const CREATE_TIMEOUT = 1000; // 1 second delay before creating items
let createTimer = null; // Single global timer for all pending items
let committingItems = {}; // { key: true/false } - track items currently being committed to prevent concurrent commits
let fetchCartGeneration = 0;

// Helper to convert cart structure from API to frontend format
// Structure: { providerId: { deliverysiteId: [items] } }
const convertCartToFrontendFormat = (carts, pendingItems = {}) => {
    // carts is an array of Cart objects from API
    // Convert to { providerId: { deliverysiteId: [items] } }
    const result = {};
    
    // First, add items from carts (created items)
    for (const cart of carts) {
        const providerId = cart.provider;
        const deliverysiteId = cart.deliverysite || null;
        const deliverysiteKey = deliverysiteId ? String(deliverysiteId) : 'null';
        
        if (!result[providerId]) {
            result[providerId] = {};
        }
        if (!result[providerId][deliverysiteKey]) {
            result[providerId][deliverysiteKey] = [];
        }
        
        for (const item of cart.items || []) {
            result[providerId][deliverysiteKey].push({
                id: item.id,
                catalogentry: item.catalogentry,
                catalogentry_data: item.catalogentry_data,
                quantity: item.count, // Map count to quantity for frontend compatibility
                unit_amount: item.unit_amount,
                unit_amount_includes_vat: item.unit_amount_includes_vat,
                unit_discount: item.unit_discount,
                vat_rate: item.vat_rate,
                unit_amount_excl_vat: item.unit_amount_excl_vat,
                unit_amount_incl_vat: item.unit_amount_incl_vat,
                unit_vat_amount: item.unit_vat_amount,
                total_amount_excl_vat: item.total_amount_excl_vat,
                total_amount_incl_vat: item.total_amount_incl_vat,
                total_vat_amount: item.total_vat_amount,
                pending: false, // Created items are not pending
                displayQuantity: item.count, // Display quantity (may be updated with pendingQuantity)
                deliverysite: deliverysiteId, // Store deliverysite for reference
            });
        }
    }
    
    // Then, merge pending items with created items
    for (const providerId in pendingItems) {
        if (!result[providerId]) {
            result[providerId] = {};
        }
        
        for (const deliverysiteKey in pendingItems[providerId]) {
            if (!result[providerId][deliverysiteKey]) {
                result[providerId][deliverysiteKey] = [];
            }
            
            for (const catalogentryKey in pendingItems[providerId][deliverysiteKey]) {
                // Convert catalogentry key to number (object keys are always strings)
                const catalogentry = parseInt(catalogentryKey, 10);
                const pendingItem = pendingItems[providerId][deliverysiteKey][catalogentryKey];
                
                // Check if there's already a created item with the same catalogentry
                // Compare as numbers to handle both string and number formats
                const existingItemIndex = result[providerId][deliverysiteKey].findIndex(
                    item => Number(item.catalogentry) === catalogentry && !item.pending
                );
                
                if (existingItemIndex >= 0) {
                    // Item exists on server, add pending quantity to it
                    const existingItem = result[providerId][deliverysiteKey][existingItemIndex];
                    result[providerId][deliverysiteKey][existingItemIndex] = {
                        ...existingItem,
                        pendingQuantity: pendingItem.quantity, // Track pending quantity to add
                        // Display total quantity (created + pending)
                        displayQuantity: addQty(existingItem.quantity || 0, pendingItem.quantity),
                    };
                } else {
                    // Add as pending item (not yet created on server)
                    result[providerId][deliverysiteKey].push({
                        catalogentry: catalogentry, // Now it's a number
                        catalogentry_data: pendingItem.catalogentry_data,
                        quantity: pendingItem.quantity,
                        pending: true, // Mark as pending
                        pendingQuantity: pendingItem.quantity, // Track quantity to create
                        displayQuantity: pendingItem.quantity, // Display quantity
                        deliverysite: deliverysiteKey === 'null' ? null : parseInt(deliverysiteKey, 10),
                    });
                }
            }
        }
    }
    
    return result;
};

// Helper to convert frontend format to API format
const convertItemToApiFormat = (item, providerId, deliverysiteId = null) => {
    // Use deliverysiteId parameter if provided, otherwise try to get it from item
    const deliverysite = deliverysiteId !== null && deliverysiteId !== undefined 
        ? deliverysiteId 
        : (item.deliverysite !== null && item.deliverysite !== undefined ? item.deliverysite : null);
    
    return {
        catalogentry: item.catalogentry,
        provider: providerId,
        deliverysite: deliverysite,
        count: item.quantity || item.count || item.pendingQuantity || 0,
    };
};

// Helper to calculate total items count (including pending)
const calculateTotalItems = (items, pendingItems = {}) => {
    let total = new Decimal(0);

    for (const providerId in items) {
        for (const deliverysiteKey in items[providerId]) {
            for (const item of items[providerId][deliverysiteKey]) {
                if (!item.pending) {
                    total = total.plus(toQtyDecimal(item.quantity));
                }
            }
        }
    }

    for (const providerId in pendingItems) {
        for (const deliverysiteKey in pendingItems[providerId]) {
            for (const catalogentry in pendingItems[providerId][deliverysiteKey]) {
                total = total.plus(
                    toQtyDecimal(pendingItems[providerId][deliverysiteKey][catalogentry].quantity)
                );
            }
        }
    }

    const normalized = total.toDecimalPlaces(3, Decimal.ROUND_DOWN);
    if (normalized.lt(0)) {
        return 0;
    }
    return parseFloat(normalized.toString());
};

// Commit all pending changes
const commitAllPendingItems = async (state, dispatch) => {
    // Collect all pending items (make a snapshot to avoid issues during iteration)
    const pendingItemsToCommit = [];
    
    for (const providerId in state.pendingItems) {
        for (const deliverysiteKey in state.pendingItems[providerId]) {
            for (const catalogentryKey in state.pendingItems[providerId][deliverysiteKey]) {
                const pendingItem = state.pendingItems[providerId][deliverysiteKey][catalogentryKey];
                if (pendingItem) {
                    const catalogentry = typeof pendingItem.catalogentry === 'string' 
                        ? parseInt(pendingItem.catalogentry, 10) 
                        : pendingItem.catalogentry;
                    const deliverysiteId = deliverysiteKey === 'null' ? null : parseInt(deliverysiteKey, 10);
                    pendingItemsToCommit.push({
                        providerId: parseInt(providerId, 10),
                        catalogentry,
                        deliverysiteId,
                    });
                }
            }
        }
    }
    
    if (pendingItemsToCommit.length === 0) {
        persistPendingTargets(state);
        return;
    }
    
    // Commit each pending item
    for (const { providerId, catalogentry, deliverysiteId } of pendingItemsToCommit) {
        await commitPendingItem(state, dispatch, providerId, catalogentry, deliverysiteId);
    }

    persistPendingTargets(state);
    
    // Fetch carts to get updated data after all commits
    await dispatch('fetchCart');
};

// Commit pending changes for a single item
const commitPendingItem = async (state, dispatch, providerId, catalogentry, deliverysiteId = null) => {
    // Normalize catalogentry to number
    const catalogentryNum = typeof catalogentry === 'string' 
        ? parseInt(catalogentry, 10) 
        : catalogentry;
    // Use string key for object access (object keys are always strings)
    const catalogentryKey = String(catalogentryNum);
    const deliverysiteKey = deliverysiteId ? String(deliverysiteId) : 'null';
    
    const commitKey = `${providerId}_${deliverysiteKey}_${catalogentryNum}`;
    
    // Prevent concurrent commits for the same item
    if (committingItems[commitKey]) {
        // Already committing this item, skip
        return;
    }
    
    // Check if item still exists in pending (it might have been removed by another commit)
    const pendingItem = state.pendingItems[providerId]?.[deliverysiteKey]?.[catalogentryKey];
    if (!pendingItem) {
        // Item was removed from pending, nothing to do
        return;
    }
    

    const pendingQuantity = pendingItem.quantity;
    const apiProviderId = resolveProviderId(providerId, pendingItem) || providerId;
        
        // Find if item exists in cart (has an id) - find cart with matching provider and deliverysite
        const cart = findCart(state.cartsList, apiProviderId, deliverysiteId);
        const existingItem = cart?.items?.find(i => Number(i.catalogentry) === catalogentryNum && i.id);
    const old_count = existingItem?.count || 0;
    
    try {
        // Mark as committing
        committingItems[commitKey] = true;
        
        if (existingItem && existingItem.id) {
            let newCount = addQty(existingItem.count || 0, pendingQuantity);
            if (toQtyDecimal(newCount).lte(0)) {
                newCount = 0;
            }
            existingItem.count = newCount;

            if (toQtyDecimal(newCount).lte(0)) {
                // Quantity becomes 0 or negative: delete the item
                await axios.delete(`/api/cartitems/${existingItem.id}/`);
                // Remove from pending after deletion
                if (state.pendingItems[providerId]?.[deliverysiteKey]?.[catalogentryKey]) {
                    Vue.delete(state.pendingItems[providerId][deliverysiteKey], catalogentryKey);
                    if (Object.keys(state.pendingItems[providerId][deliverysiteKey]).length === 0) {
                        Vue.delete(state.pendingItems[providerId], deliverysiteKey);
                    }
                    if (Object.keys(state.pendingItems[providerId]).length === 0) {
                        Vue.delete(state.pendingItems, providerId);
                    }
                }
                // Don't remove deleted flag here - let setCarts clean it up after fetchCart confirms deletion
                // This ensures flags are preserved during rapid deletions
            } else {
                
                // Remove from pending BEFORE updating state to avoid double counting
                if (state.pendingItems[providerId]?.[deliverysiteKey]?.[catalogentryKey]) {
                    Vue.delete(state.pendingItems[providerId][deliverysiteKey], catalogentryKey);
                    if (Object.keys(state.pendingItems[providerId][deliverysiteKey]).length === 0) {
                        Vue.delete(state.pendingItems[providerId], deliverysiteKey);
                    }
                    if (Object.keys(state.pendingItems[providerId]).length === 0) {
                        Vue.delete(state.pendingItems, providerId);
                    }
                }

                // Update quantity
                const response = await axios.patch(`/api/cartitems/${existingItem.id}/`, {
                    count: newCount,
                });
                
                // Update state with server response (pending already removed)
                if (response.data) {
                    dispatch('updateItemFromServer', {
                        ...response.data,
                        deliverysite: deliverysiteId,
                    });
                }
            }
        } else {
            if (toQtyDecimal(pendingQuantity).gt(0)) {
                const apiItem = convertItemToApiFormat(pendingItem, apiProviderId, deliverysiteId);
                const response = await axios.post('/api/cartitems/', apiItem);
                
                // Remove from pending BEFORE updating state to avoid double counting
                if (state.pendingItems[providerId]?.[deliverysiteKey]?.[catalogentryKey]) {
                    Vue.delete(state.pendingItems[providerId][deliverysiteKey], catalogentryKey);
                    if (Object.keys(state.pendingItems[providerId][deliverysiteKey]).length === 0) {
                        Vue.delete(state.pendingItems[providerId], deliverysiteKey);
                    }
                    if (Object.keys(state.pendingItems[providerId]).length === 0) {
                        Vue.delete(state.pendingItems, providerId);
                    }
                }
                
                // Item created, update state (pending already removed)
                if (response.data) {
                    dispatch('updateItemFromServer', {
                        ...response.data,
                        deliverysite: deliverysiteId,
                    });
                }
            } else {
                // If pendingQuantity is negative and item doesn't exist, just remove from pending
                if (state.pendingItems[providerId]?.[deliverysiteKey]?.[catalogentryKey]) {
                    Vue.delete(state.pendingItems[providerId][deliverysiteKey], catalogentryKey);
                    if (Object.keys(state.pendingItems[providerId][deliverysiteKey]).length === 0) {
                        Vue.delete(state.pendingItems[providerId], deliverysiteKey);
                    }
                    if (Object.keys(state.pendingItems[providerId]).length === 0) {
                        Vue.delete(state.pendingItems, providerId);
                    }
                }
            }
        }
    } catch (error) {
        console.error('Error committing pending item:', error);
        // On error, keep the item in pending list
    } finally {
        // Always clear the committing flag
        delete committingItems[commitKey];
        persistPendingTargets(state);
    }
};

// Schedule commit of all pending changes
const scheduleItemCommit = (state, dispatch) => {
    // Clear existing global timer if any
    if (createTimer) {
        clearTimeout(createTimer);
        createTimer = null;
    }
    
    // Schedule commit of all pending items (will be called after CREATE_TIMEOUT if no new clicks occur)
    createTimer = setTimeout(() => {
        commitAllPendingItems(state, dispatch);
        createTimer = null;
    }, CREATE_TIMEOUT);
};

const state = {
    // Structure: { providerId: { deliverysiteId: [items] } } - items from server (created)
    items: {},
    // Structure: { providerId: { deliverysiteId: { catalogentry: { quantity, catalogentry_data, ... } } } } - pending quantities by catalogentry and deliverysite
    pendingItems: {},
    deletedItems: {}, // Structure: { providerId: { deliverysiteId: { catalogentry: true } } } - Track items marked for deletion (UI feedback)
    carts: {}, // Map of providerId -> cartId (deprecated, use cartsList instead)
    cartsList: [], // Array of Cart objects with items
    loading: false,
    error: null,
    deletingProviders: {}, // { providerId: true/false } - track providers being deleted
    clearingCart: false, // Track if cart is being cleared
};

const mutations = {
    setItems(state, { items, pendingItems = {} }) {
        state.items = items;
        state.pendingItems = pendingItems || {};
        // Update cartItems in session store
        const totalCount = calculateTotalItems(items, pendingItems);
        store.commit('session/updateCartItems', totalCount);
    },
    markItemAsDeleted(state, { providerId, catalogentry, deliverysiteId = null }) {
        // Normalize catalogentry to number
        const catalogentryNum = typeof catalogentry === 'string' 
            ? parseInt(catalogentry, 10) 
            : catalogentry;
        const catalogentryKey = String(catalogentryNum);
        const deliverysiteKey = deliverysiteId ? String(deliverysiteId) : 'null';
        
        // Initialize deletedItems structure if needed
        if (!state.deletedItems[providerId]) {
            Vue.set(state.deletedItems, providerId, {});
        }
        if (!state.deletedItems[providerId][deliverysiteKey]) {
            Vue.set(state.deletedItems[providerId], deliverysiteKey, {});
        }
        
        // Mark item as deleted
        Vue.set(state.deletedItems[providerId][deliverysiteKey], catalogentryKey, true);
    },
    removeDeletedFlag(state, { providerId, catalogentry, deliverysiteId = null }) {
        // Normalize catalogentry to number
        const catalogentryNum = typeof catalogentry === 'string' 
            ? parseInt(catalogentry, 10) 
            : catalogentry;
        const catalogentryKey = String(catalogentryNum);
        const deliverysiteKey = deliverysiteId ? String(deliverysiteId) : 'null';
        
        // Remove deleted flag
        if (state.deletedItems[providerId]?.[deliverysiteKey]?.[catalogentryKey]) {
            Vue.delete(state.deletedItems[providerId][deliverysiteKey], catalogentryKey);
            // Clean up empty objects
            if (Object.keys(state.deletedItems[providerId][deliverysiteKey]).length === 0) {
                Vue.delete(state.deletedItems[providerId], deliverysiteKey);
            }
            if (Object.keys(state.deletedItems[providerId]).length === 0) {
                Vue.delete(state.deletedItems, providerId);
            }
        }
    },
    setCarts(state, carts) {
        // carts is an array of Cart objects
        const cartMap = {};
        for (const cart of carts) {
            cartMap[cart.provider] = cart.id;
        }
        state.carts = cartMap;
        state.cartsList = carts; // Store full cart objects
        
        // Clean up deletedItems flags for items that no longer exist in server data
        // This ensures items marked as deleted stay hidden until server confirms they're gone
        // This is especially important for rapid deletions to prevent items from reappearing
        const itemsToClean = [];
        for (const providerId in state.deletedItems) {
            for (const deliverysiteKey in state.deletedItems[providerId]) {
                for (const catalogentryKey in state.deletedItems[providerId][deliverysiteKey]) {
                    const catalogentryNum = parseInt(catalogentryKey, 10);
                    const deliverysiteId = deliverysiteKey === 'null' ? null : parseInt(deliverysiteKey, 10);
                    
                    // Check if item still exists in server data
                    const cart = carts.find(c => 
                        c.provider === parseInt(providerId, 10) && 
                        (c.deliverysite === deliverysiteId || (c.deliverysite === null && deliverysiteId === null))
                    );
                    const itemExists = cart?.items?.some(item => 
                        item.catalogentry === catalogentryNum && item.id
                    );
                    
                    // If item doesn't exist in server data, it's safe to remove the deleted flag
                    if (!itemExists) {
                        itemsToClean.push({ providerId, deliverysiteKey, catalogentryKey });
                    }
                }
            }
        }
        
        // Clean up deleted flags for items confirmed deleted
        for (const { providerId, deliverysiteKey, catalogentryKey } of itemsToClean) {
            Vue.delete(state.deletedItems[providerId][deliverysiteKey], catalogentryKey);
            if (Object.keys(state.deletedItems[providerId][deliverysiteKey]).length === 0) {
                Vue.delete(state.deletedItems[providerId], deliverysiteKey);
            }
            if (Object.keys(state.deletedItems[providerId]).length === 0) {
                Vue.delete(state.deletedItems, providerId);
            }
        }
    },
    setLoading(state, loading) {
        state.loading = loading;
    },
    setError(state, error) {
        state.error = error;
    },
    setDeletingProvider(state, { providerId, deleting }) {
        Vue.set(state.deletingProviders, providerId, deleting);
    },
    setClearingCart(state, clearing) {
        state.clearingCart = clearing;
    },
    addDelta(state, { providerId, item }) {
        let delta = item.quantity;
        const deliverysiteId = item.deliverysite || null;
        const deliverysiteKey = deliverysiteId ? String(deliverysiteId) : 'null';

        if (!state.pendingItems[providerId]) {
            Vue.set(state.pendingItems, providerId, {});
        }
        if (!state.pendingItems[providerId][deliverysiteKey]) {
            Vue.set(state.pendingItems[providerId], deliverysiteKey, {});
        }
        const catalogentry = typeof item.catalogentry === 'string' 
            ? parseInt(item.catalogentry, 10) 
            : item.catalogentry;

        const catalogentryKey = String(catalogentry);
        // Find cart with matching provider and deliverysite
        const cart = findCart(state.cartsList, providerId, deliverysiteId);
        
        const existingItem = cart?.items?.find(i => Number(i.catalogentry) === catalogentry && i.id);
        const pendingItem = state.pendingItems[providerId]?.[deliverysiteKey]?.[catalogentryKey];
        const currentFinalQuantity = toQtyDecimal(pendingItem?.quantity).plus(
            toQtyDecimal(existingItem?.count)
        );
        let deltaDecimal = toQtyDecimal(delta);

        if (currentFinalQuantity.plus(deltaDecimal).lt(0)) {
            deltaDecimal = currentFinalQuantity.negated();
        }
        const adjustedDelta = formatQty(deltaDecimal);

        if (pendingItem) {
            pendingItem.quantity = formatQty(toQtyDecimal(pendingItem.quantity).plus(deltaDecimal));
        } else {
            Vue.set(state.pendingItems[providerId][deliverysiteKey], catalogentryKey, {
                catalogentry: catalogentry,
                catalogentry_data: item.catalogentry_data,
                quantity: adjustedDelta,
                deliverysite: deliverysiteId,
            });
        }

        // Update items state for display
        const items = convertCartToFrontendFormat(state.cartsList, state.pendingItems);
        state.items = items;
        // Update cartItems count immediately in session store
        const totalCount = calculateTotalItems(state.items, state.pendingItems);
        store.commit('session/updateCartItems', totalCount);
        persistPendingTargets(state);
    },
    removeItem(state, { providerId, catalogentry, deliverysiteId = null, quantity = 1 }) {
        // Normalize catalogentry to number
        const catalogentryNum = typeof catalogentry === 'string' 
            ? parseInt(catalogentry, 10) 
            : catalogentry;
        // Use string key for object access (object keys are always strings)
        const catalogentryKey = String(catalogentryNum);
        const deliverysiteKey = deliverysiteId ? String(deliverysiteId) : 'null';
        
        // Calculate available quantity (created + pending)
        let availableQuantity = new Decimal(0);

        const cart = state.cartsList.find(c => 
            c.provider === providerId && 
            (c.deliverysite === deliverysiteId || (c.deliverysite === null && deliverysiteId === null))
        );
        if (cart && cart.items) {
            const existingItem = cart.items.find(i => i.catalogentry === catalogentryNum && i.id);
            if (existingItem) {
                availableQuantity = availableQuantity.plus(toQtyDecimal(existingItem.count));
            }
        }

        const pendingItem = state.pendingItems[providerId]?.[deliverysiteKey]?.[catalogentryKey];
        if (pendingItem) {
            availableQuantity = availableQuantity.plus(toQtyDecimal(pendingItem.quantity));
        }

        if (availableQuantity.lt(0)) {
            availableQuantity = new Decimal(0);
        }

        const quantityToRemoveDecimal = Decimal.min(
            toQtyDecimal(quantity),
            availableQuantity
        );

        if (quantityToRemoveDecimal.lte(0)) {
            return;
        }
        const quantityToRemove = parseFloat(quantityToRemoveDecimal.toString());
        
        // Initialize pendingItems structure if needed
        if (!state.pendingItems[providerId]) {
            Vue.set(state.pendingItems, providerId, {});
        }
        if (!state.pendingItems[providerId][deliverysiteKey]) {
            Vue.set(state.pendingItems[providerId], deliverysiteKey, {});
        }
        
        // Check if item exists in pending
        if (state.pendingItems[providerId][deliverysiteKey][catalogentryKey]) {
            const newPendingQty = formatQty(
                toQtyDecimal(
                    state.pendingItems[providerId][deliverysiteKey][catalogentryKey].quantity
                ).minus(quantityToRemoveDecimal)
            );
            if (toQtyDecimal(newPendingQty).eq(0)) {
                Vue.delete(state.pendingItems[providerId][deliverysiteKey], catalogentryKey);
                if (Object.keys(state.pendingItems[providerId][deliverysiteKey]).length === 0) {
                    Vue.delete(state.pendingItems[providerId], deliverysiteKey);
                }
                if (Object.keys(state.pendingItems[providerId]).length === 0) {
                    Vue.delete(state.pendingItems, providerId);
                }
            } else {
                state.pendingItems[providerId][deliverysiteKey][catalogentryKey].quantity = newPendingQty;
            }
        } else {
            // Item doesn't exist in pending: check if it exists in cart
            const existingItem = cart?.items?.find(i => i.catalogentry === catalogentryNum && i.id);
            
            if (existingItem) {
                // Item exists in cart with id: add negative quantity to pending
                Vue.set(state.pendingItems[providerId][deliverysiteKey], catalogentryKey, {
                    catalogentry: catalogentryNum,
                    catalogentry_data: existingItem.catalogentry_data,
                    quantity: formatQty(quantityToRemoveDecimal.negated()),
                });
            } else {
                // Item doesn't exist anywhere: nothing to remove, do nothing
                return;
            }
        }
        
        // Update items state for display
        const items = convertCartToFrontendFormat(state.cartsList, state.pendingItems);
        state.items = items;
        
        // Update cartItems count immediately in session store
        const totalCount = calculateTotalItems(state.items, state.pendingItems);
        store.commit('session/updateCartItems', totalCount);
        persistPendingTargets(state);
    },
    removeProvider(state, providerId) {
        // Remove from cartsList
        state.cartsList = state.cartsList.filter(c => c.provider !== providerId);
        
        // Remove pending items
        if (state.pendingItems[providerId]) {
            Vue.delete(state.pendingItems, providerId);
        }
        
        // Remove deleted items
        if (state.deletedItems[providerId]) {
            Vue.delete(state.deletedItems, providerId);
        }
        
        // Remove from old items structure
        if (state.items[providerId]) {
            Vue.delete(state.items, providerId);
        }
        // Remove from old carts map
        if (state.carts[providerId]) {
            Vue.delete(state.carts, providerId);
        }
        
        // Update cartItems count immediately in session store
        const totalCount = calculateTotalItems(state.items, state.pendingItems);
        store.commit('session/updateCartItems', totalCount);
        persistPendingTargets(state);
    },
    clearCart(state) {
        state.items = {};
        state.pendingItems = {};
        state.deletedItems = {};
        state.carts = {};
        state.cartsList = [];
        store.commit('session/updateCartItems', 0);
        persistPendingTargets(state);
    },
    updateItemFromServer(state, serverItem) {
        const deliverysiteId = Object.prototype.hasOwnProperty.call(serverItem, 'deliverysite')
            ? serverItem.deliverysite
            : undefined;

        const mergeItem = (cart) => {
            const items = [...(cart.items || [])];
            const itemIndex = items.findIndex((item) => item.id === serverItem.id);
            if (itemIndex >= 0) {
                items[itemIndex] = {
                    ...items[itemIndex],
                    ...serverItem,
                };
            } else {
                items.push(serverItem);
            }
            return {
                ...cart,
                items,
            };
        };

        const cartIndex = state.cartsList.findIndex((cart) => (
            (serverItem.cart && cart.id === serverItem.cart)
            || (cart.items || []).some((item) => item.id === serverItem.id)
        ));

        let updatedCartsList;
        if (cartIndex >= 0) {
            updatedCartsList = state.cartsList.map((cart, index) => (
                index === cartIndex ? mergeItem(cart) : cart
            ));
        } else if (serverItem.cart) {
            updatedCartsList = [
                ...state.cartsList,
                {
                    id: serverItem.cart,
                    provider: serverItem.provider,
                    provider_data: serverItem.provider_data || null,
                    deliverysite: deliverysiteId === undefined ? null : deliverysiteId,
                    items: [serverItem],
                },
            ];
        } else {
            return;
        }

        state.cartsList = updatedCartsList;
        const items = convertCartToFrontendFormat(updatedCartsList, state.pendingItems);
        state.items = items;
        store.commit('session/updateCartItems', calculateTotalItems(items, state.pendingItems));
    },
    restorePendingFromStorage(state) {
        if (pendingItemsExist(state.pendingItems)) {
            return;
        }
        const targets = loadStoredPendingTargets();
        if (!targets.length) {
            return;
        }
        state.pendingItems = pendingItemsFromTargets(state.cartsList, targets);
        if (!pendingItemsExist(state.pendingItems)) {
            orgStorage.removeItem(CART_PENDING_STORAGE_KEY);
        }
    },
};

const actions = {
    updateItemFromServer({ commit }, serverItem) {
        commit('updateItemFromServer', serverItem);
    },
    async fetchCart({ commit, state, rootGetters }) {
        if (
            !resolvePermissionCheck(
                rootGetters['session/current_user_permissions'],
                'order.add_providerorder'
            )
        ) {
            return;
        }

        const generation = ++fetchCartGeneration;
        commit('setLoading', true);
        commit('setError', null);
        
        try {
            const response = await axios.get('/api/carts/', { params: { limit: 10000 } });
            if (generation !== fetchCartGeneration) {
                return;
            }
            const carts = response.data.results || response.data || [];
            
            // Store cart IDs
            commit('setCarts', carts);
            commit('restorePendingFromStorage');
            
            // Convert to frontend format (merge with pending items)
            const items = convertCartToFrontendFormat(carts, state.pendingItems);
            commit('setItems', { items, pendingItems: state.pendingItems });
        } catch (xhr_error) {
            if (generation !== fetchCartGeneration) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('setError', error);
            console.error('Error fetching cart:', error);
        } finally {
            if (generation === fetchCartGeneration) {
                commit('setLoading', false);
            }
        }
    },
    async flushPendingAndFetch({ state, dispatch }) {
        if (createTimer) {
            clearTimeout(createTimer);
            createTimer = null;
        }
        await dispatch('fetchCart');
        if (pendingItemsExist(state.pendingItems)) {
            await commitAllPendingItems(state, dispatch);
        }
    },
    
    async addItem({ commit, state, dispatch, rootGetters }, { providerId, item }) {
        // The cart is only used to create provider orders, which requires the
        // `write_providerorders` scope (canonical `order.add_providerorder`).
        if (!resolvePermissionCheck(rootGetters['session/current_user_permissions'], 'order.add_providerorder')) {
            return;
        }

        const resolvedProviderId = resolveProviderId(providerId, item);
        if (!resolvedProviderId) {
            console.error('Cannot add cart item without provider');
            return;
        }

        // Clear existing global timer if any
        if (createTimer) {
            clearTimeout(createTimer);
            createTimer = null;
        }
        
        // Add item to pending (mutation handles the logic and updates pending quantity)
        commit('addDelta', { providerId: resolvedProviderId, item });
        
        // Schedule commit of all pending changes with sliding timeout
        // This recreates the global timeout after modifying the pending value
        scheduleItemCommit(state, dispatch);
    },
    async removeProvider({ commit, state, dispatch }, providerId) {
        // Find the cart for this provider
        const cart = state.cartsList.find(c => c.provider === providerId);
        
        if (!cart || !cart.id) {
            // No cart to delete, just remove from state
            commit('removeProvider', providerId);
            return;
        }
        
        // Set loading state for this provider
        commit('setDeletingProvider', { providerId, deleting: true });
        
        try {
            // Delete the cart on server first (which will cascade delete all items)
            await axios.delete(`/api/carts/${cart.id}/`);
            
            // Only remove from store after successful server deletion
            commit('removeProvider', providerId);
            
            // Fetch stats to sync
            await dispatch('session/fetchStats', {}, { root: true });
        } catch (error) {
            console.error('Error removing provider cart:', error);
            // Don't remove from store if server deletion failed
        } finally {
            // Clear loading state
            commit('setDeletingProvider', { providerId, deleting: false });
        }
    },
    
    async clearCart({ commit, state, dispatch }) {
        // Get all carts with IDs
        const carts = state.cartsList.filter(cart => cart.id);
        
        if (carts.length === 0) {
            // No carts to delete, just clear state
            commit('clearCart');
            return;
        }
        
        // Set loading state
        commit('setClearingCart', true);
        
        try {
            // Delete all carts on server first (which will cascade delete all items)
            for (const cart of carts) {
                await axios.delete(`/api/carts/${cart.id}/`);
            }
            
            // Only clear store after successful server deletion
            commit('clearCart');
            
            // Fetch stats to sync
            await dispatch('session/fetchStats', {}, { root: true });
        } catch (error) {
            console.error('Error clearing cart:', error);
            // Don't clear store if server deletion failed
        } finally {
            // Clear loading state
            commit('setClearingCart', false);
        }
    },
};

const getters = {
    // Get total number of items in cart (including pending)
    totalItemsCount(state) {
        let count = 0;
        for (const cart of state.cartsList) {
            count += (cart.items || []).length;
        }
        // Add pending items count (only items not yet created)
        for (const providerId in state.pendingItems) {
            for (const deliverysiteKey in state.pendingItems[providerId]) {
                for (const catalogentryKey in state.pendingItems[providerId][deliverysiteKey]) {
                    const catalogentryNum = parseInt(catalogentryKey, 10);
                    const deliverysiteId = deliverysiteKey === 'null' ? null : parseInt(deliverysiteKey, 10);
                    const cart = findCart(state.cartsList, providerId, deliverysiteId);
                    const existingItem = cart?.items?.find(i => Number(i.catalogentry) === catalogentryNum && i.id);
                    if (!existingItem) {
                        count += 1;
                    }
                }
            }
        }
        return count;
    },
    hasPendingOperations(state) {
        return pendingItemsExist(state.pendingItems);
    },
    displayCarts(state) {
        const carts = [...state.cartsList];
        const seen = new Set(
            carts.map((cart) => cartGroupKey(cart.provider, cart.deliverysite))
        );

        for (const providerId in state.pendingItems || {}) {
            for (const deliverysiteKey in state.pendingItems[providerId] || {}) {
                const pendingGroup = state.pendingItems[providerId][deliverysiteKey] || {};
                if (!Object.keys(pendingGroup).length) {
                    continue;
                }
                const key = cartGroupKey(providerId, deliverysiteKey);
                if (seen.has(key)) {
                    continue;
                }
                seen.add(key);
                const firstPending = Object.values(pendingGroup)[0] || {};
                carts.push({
                    id: `pending-${key}`,
                    provider: Number(providerId),
                    provider_data: firstPending.catalogentry_data?.provider_data
                        || firstPending.provider_data
                        || null,
                    deliverysite: deliverysiteKey === 'null' ? null : parseInt(deliverysiteKey, 10),
                    items: [],
                    total_amount_excl_vat: null,
                    total_amount_incl_vat: null,
                    total_vat_amount: null,
                });
            }
        }
        return carts;
    },
    // Get total quantity of items in cart (including pending)
    totalQuantity(state) {
        return calculateTotalItems(state.items, state.pendingItems);
    },
    // Get cart items grouped by provider (including pending)
    groupedItems(state) {
        return convertCartToFrontendFormat(state.cartsList, state.pendingItems);
    },
    // Get all totals across all carts
    getAllTotals(state) {
        let totalExclVat = 0;
        let totalInclVat = 0;
        let totalVat = 0;

        for (const cart of state.cartsList) {
            // Convert string values to numbers (CurrencyField returns strings)
            const exclVat = parseFloat(cart.total_amount_excl_vat) || 0;
            const inclVat = parseFloat(cart.total_amount_incl_vat) || 0;
            const vat = parseFloat(cart.total_vat_amount) || 0;
            
            totalExclVat += exclVat;
            totalInclVat += inclVat;
            totalVat += vat;
        }

        return {
            total_amount_excl_vat: totalExclVat,
            total_amount_incl_vat: totalInclVat,
            total_vat_amount: totalVat,
        };
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};

if (typeof window !== 'undefined') {
    const persistOnUnload = () => {
        persistPendingTargets(store.state.cart);
    };
    window.addEventListener('pagehide', persistOnUnload);
    window.addEventListener('beforeunload', persistOnUnload);
}
